"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Truck, Zap, SlidersHorizontal, CalendarCheck, ArrowRight, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const rentalInclusions = [{
  id: "delivery",
  title: "Delivery",
  description: "Delivery timing, positioning, site access, and setup requirements are confirmed with each quote. Requirements may vary based on trailer size and delivery location.",
  iconName: "truck"
}, {
  id: "cables",
  title: "Pickup",
  description: "Pickup timing is scheduled and confirmed with each rental. Weekend and holiday scheduling is subject to availability.",
  iconName: "zap"
}, {
  id: "orientation",
  title: "Quote-Based Pricing",
  description: "Pricing is based on trailer size, requested dates, delivery location, site power availability, and optional add-ons.",
  iconName: "sliders"
}, {
  id: "pickup",
  title: "Trailer Options",
  description: "Choose from 6×8, 7×12, and 7×16 refrigerated and freezer trailers, plus a 53-foot reefer trailer. Availability, specifications, and requirements for the 53-foot reefer are confirmed individually.",
  iconName: "calendar"
}];
export default function Inclusions() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "truck":
        return <Truck className="size-6 text-foreground" />;
      case "zap":
        return <Zap className="size-6 text-foreground" />;
      case "sliders":
        return <SlidersHorizontal className="size-6 text-foreground" />;
      case "calendar":
        return <CalendarCheck className="size-6 text-foreground" />;
      default:
        return <ShieldCheck className="size-6 text-foreground" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="whats-included" data-nav="light" className="bg-background text-foreground py-16 md:py-24 lg:py-28 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold uppercase tracking-wider mb-4 border border-border">
            <span className="size-2 rounded-full bg-primary animate-pulse"></span>
            What to Expect
          </div>

          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground text-balance mb-4">
            Rental Details
          </h2>

          <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed text-balance">
            Delivery, positioning, setup, and pickup arrangements are scheduled and confirmed with each quote. Site-access and delivery requirements for the 53-foot reefer trailer are evaluated individually.
          </p>
        </div>

        {/* 4-Column Inclusions Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {rentalInclusions.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} className="group relative flex flex-col justify-between rounded-lg border border-border bg-card p-6 shadow-xs hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-200">
              <div>
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div className="flex items-center justify-center size-12 rounded-lg bg-muted border border-border group-hover:bg-accent/40 transition-colors duration-200">
                    {getIcon(item.iconName)}
                  </div>
                </div>

                <h3 className="font-default text-lg font-semibold text-foreground tracking-tight mb-2.5">
                  {item.title}
                </h3>

                <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Bottom Support Banner & Dual Action Bar */}
        <div className="mt-12 md:mt-16 rounded-lg border border-border bg-muted/40 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left">
            <div className="p-3 rounded-md bg-card border border-border shrink-0">
              <ShieldCheck className="size-6 text-primary" />
            </div>
            <div>
              <h4 className="font-default text-base md:text-lg font-semibold text-foreground">
                Need help choosing the right trailer?
              </h4>
              <p className="font-sans text-sm text-muted-foreground mt-0.5">
                Trailer size, rental dates, delivery location, site access, power needs, optional equipment, and 53-foot reefer requirements are confirmed with each quote.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto shadow-sm">
              <Link to="/ContactRequestAQuotePage">
                <span>Request a Quote</span>
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
            <a href="tel:2135218821" className="inline-flex items-center justify-center gap-2 h-10 rounded-md border border-border bg-card px-4 text-sm font-medium text-muted-foreground w-full sm:w-auto hover:text-foreground transition-colors">
              <span>213-521-8821</span>
            </a>
          </div>
        </div>
      </div>
    </section>;
}